/**
 * Companion Panels — crisis, professional support, usage, disclaimer, consent.
 *
 * Ported from Profound Living's coaching panels and re-toned for this app's
 * cloud-studies canvas (charcoal and gunmetal grounds, gold accents). The
 * crisis and professional cards keep their red and amber families — safety
 * surfaces should look like safety surfaces, not like decoration.
 *
 * Australian English throughout.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Phone, MessageCircle, ExternalLink, Heart, ShieldCheck, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { coachCardLines, textLineSmsHref } from '../../../../../packages/crisis-support/src/textLines.js';

import { Capacitor } from '@capacitor/core';
export function CrisisResourcesCard({ resources }) {
  if (!resources?.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-red-400 bg-red-950/60 p-5 shadow-lg"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="flex-shrink-0 mt-1 text-red-300" size={22} />
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-red-100">
            Please speak to someone right now
          </h3>
          <p className="text-sm sm:text-base text-red-100/90 mt-1 leading-relaxed">
            What you&apos;re carrying deserves real, human support. Please reach out. They&apos;re trained
            for exactly this, and most of these lines are there around the clock; each one shows its hours.
          </p>
          <ul className="mt-3 space-y-2">
            {coachCardLines(resources).map((r) => (
              <li key={r.name} className="text-sm sm:text-base">
                <span className="font-semibold text-red-100">{r.name}</span>
                <span className="text-red-100/90">: {r.detail}</span>
                {r.phone && (
                  <a
                    href={`tel:${r.phone.replace(/\s/g, '')}`}
                    className="ml-2 inline-flex items-center gap-1 font-bold text-red-200 hover:text-white underline"
                  >
                    <Phone size={14} /> {r.phone}
                  </a>
                )}
                {r.text && (textLineSmsHref(r.text) ? (
                  <a href={textLineSmsHref(r.text)} className="ml-2 inline-flex items-center gap-1 font-bold text-red-200 hover:text-white underline">
                    <MessageCircle size={14} /> {r.text}
                  </a>
                ) : (
                  <span className="ml-2 inline-flex items-center gap-1 font-bold text-red-200">
                    <MessageCircle size={14} /> {r.text}
                  </span>
                ))}
                {r.url && (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 inline-flex items-center gap-1 text-red-200 hover:text-white"
                    aria-label={`${r.name} website`}
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

export function ProfessionalSupportCard({ support }) {
  if (!support) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-amber-500/60 bg-amber-950/40 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <Heart className="flex-shrink-0 mt-1 text-amber-300" size={20} />
        <div className="flex-1">
          <h4 className="font-semibold text-amber-200 text-sm sm:text-base">
            Worth considering: {support.type}
          </h4>
          <p className="text-sm sm:text-base text-amber-100/90 mt-1 leading-relaxed">{support.rationale}</p>
          {support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm sm:text-base text-amber-100/90 list-disc pl-5">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          {support.country && support.country !== 'INTERNATIONAL' && (
            <p className="text-xs text-amber-200/80 mt-2">
              Showing resources for {support.country}.
            </p>
          )}
          <p className="text-xs sm:text-sm text-amber-200/80 mt-3 italic">{support.disclaimer}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function SubtleProfessionalHint({ support }) {
  const [expanded, setExpanded] = useState(false);
  if (!support) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-conversation-teal/40 bg-gunmetal/80 px-4 py-3"
    >
      <div className="flex items-start gap-2">
        <Info className="flex-shrink-0 mt-0.5 text-conversation-teal" size={16} />
        <div className="flex-1 text-sm sm:text-base text-pearl/90 leading-relaxed">
          <p>{support.rationale}</p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1.5 flex items-center gap-1 text-xs sm:text-sm font-medium text-conversation-teal hover:text-white"
            aria-expanded={expanded}
          >
            How to find one
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expanded && support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs sm:text-sm text-pearl/90 list-disc pl-4">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          <p className="text-xs text-pearl/70 mt-2 italic">{support.disclaimer}</p>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Usage bar. `suppressUpgrade` inherits Willow's rule: any crisis card or
 * amber-tone professional referral on screen kills the upsell for the rest of
 * the exchange. Philosophy is a lighter room than grief, but the rule costs
 * nothing to keep and is unforgivable to need and not have.
 */
export function UsageIndicator({ usage, suppressUpgrade = false }) {
  if (!usage) return null;
  const { tier, used, dailyLimit, remaining, upgradeUrl } = usage;
  const pct = dailyLimit ? Math.min(100, (used / dailyLimit) * 100) : 0;
  // No upgrade link inside the iOS app: it points at a purchase outside the store
  // (App Store rule 3.1.1). The allowance itself still shows.
  const showUpgrade = !suppressUpgrade && !Capacitor.isNativePlatform() && tier === 'free' && upgradeUrl && remaining <= 2;

  return (
    <div className="flex items-center gap-2 text-xs sm:text-sm text-pearl/70">
      <div className="flex-1 max-w-[120px] h-1.5 bg-gunmetal rounded-full overflow-hidden">
        <div
          className={`h-full ${pct >= 80 ? 'bg-amber-400' : 'bg-bright-gold'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span>{used}/{dailyLimit} today</span>
      {showUpgrade && (
        <a href={upgradeUrl} className="ml-2 font-medium text-bright-gold hover:text-white underline">
          More time with Sofia
        </a>
      )}
    </div>
  );
}

/**
 * The standing disclaimer. No hardcoded crisis number here (Nigel, 2026-08-17):
 * a fixed Lifeline line was Australia-only in a seven-market app, and the Hub's
 * safeguards already serve verified, region-correct crisis lines — keyed to the
 * country given at consent — through CrisisResourcesCard the moment a
 * conversation warrants them. Same pattern as Willow's disclaimer.
 */
export function CompanionDisclaimer() {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gunmetal/80 border border-white/10 rounded-lg text-xs sm:text-sm text-pearl/80 leading-snug">
      <Info className="flex-shrink-0 text-pearl/60" size={15} />
      <span>
        Sofia is an AI companion, not a counsellor or therapist. If something with real personal
        weight arrives, <span className="font-medium text-pearl">she&apos;ll set the philosophy down
        and point you to human support</span>.
      </span>
    </div>
  );
}

// The Hub carries region-specific crisis lines for these seven and falls back
// to honest international directories for anything else. "Somewhere else"
// exists so a person in, say, Germany says so explicitly rather than being
// handed a UK number that does not work for them.
const COUNTRIES = [
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'IN', label: 'India' },
  { code: 'UK', label: 'United Kingdom' },
  { code: 'IE', label: 'Ireland' },
  { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' },
  { code: 'OTHER', label: 'Somewhere else' },
];

export function ConsentGate({ onAccept, onDecline }) {
  const [firstName, setFirstName] = useState('');
  const [country, setCountry] = useState('');
  const [style, setStyle] = useState('facilitative');
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFailed(null);
    const result = await onAccept({
      personalContext: {
        firstName: firstName.trim(),
        country: country || null,
        preferredCoachingStyle: style,
        communicationPreference: 'balanced',
        pace: 'moderate',
      },
      // Always app-only, for the same reasoning as Willow's gate: dataSharingScope
      // gates a cross-app feature that does not exist yet, and asking someone
      // to make a privacy decision with no effect spends trust for nothing.
      dataSharingScope: 'app-only',
    });
    if (result?.success === false) {
      setFailed(result.response || 'That didn’t save. Please try again in a moment.');
    }
    setSubmitting(false);
  };

  const field = 'w-full rounded-lg border border-white/15 bg-gunmetal text-pearl px-3 py-2 text-base focus:ring-2 focus:ring-bright-gold/70 focus:border-bright-gold/70';

  return (
    <div className="max-w-2xl mx-auto bg-charcoal border border-white/10 rounded-2xl shadow-lg p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="text-bright-gold" size={28} />
        <h2 className="text-xl sm:text-2xl font-bold text-white">Before we begin</h2>
      </div>

      <div className="space-y-3 text-sm sm:text-base text-pearl/90 leading-relaxed">
        <p>
          Sofia remembers. That&apos;s what makes her different from a search box: she can hold what
          you believe, what you&apos;ve argued, and which forks you&apos;ve answered across
          conversations, which means storing a small profile.
        </p>
        <p>
          <strong className="text-white">What&apos;s kept:</strong> what you tell her about yourself,
          your conversations with her (the messages themselves and a summary of each session), and
          the philosophical positions you take. It stays with
          Profound Philosophy and doesn&apos;t travel to your other Dive Deep Dialogues apps. You can
          read, export or delete every bit of it whenever you like, and deleting means gone.
        </p>
        <p>
          <strong className="text-white">What she isn&apos;t:</strong> a therapist, counsellor or
          doctor. Philosophy is not therapy. If what you bring her needs a professional, she&apos;ll
          say so plainly rather than quietly carrying on.
        </p>
        <p className="text-pearl/70 italic">
          Your conversations are stored in Australia. Sofia&apos;s replies come from Anthropic&apos;s
          Claude, so each message is sent to Anthropic to be answered; under Anthropic&apos;s commercial
          terms it is not used to train their models. Your conversations are never sold and never
          shared with advertisers.
        </p>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="sofia-name" className="block text-sm font-medium text-pearl mb-1">
            What should Sofia call you?
          </label>
          <input
            id="sofia-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value.slice(0, 40))}
            placeholder="First name or nickname"
            className={field}
            maxLength={40}
          />
        </div>

        <div>
          <label htmlFor="sofia-country" className="block text-sm font-medium text-pearl mb-1">
            Where are you based?{' '}
            <span className="text-pearl/60 font-normal">(so any support resources she shows you are ones you can actually ring)</span>
          </label>
          <select id="sofia-country" value={country} onChange={(e) => setCountry(e.target.value)} className={field}>
            <option value="">Select a country</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="sofia-style" className="block text-sm font-medium text-pearl mb-1">
            How would you like her to be with you?
          </label>
          {/* Default is facilitative: clue before answer IS Sofia's contract,
              and defaulting anywhere else would fight the persona from the
              first message. The others adjust the rhythm, not the character. */}
          <select id="sofia-style" value={style} onChange={(e) => setStyle(e.target.value)} className={field}>
            <option value="facilitative">Clue before answer: make me work for it</option>
            <option value="directive">Generous teacher: more map, quicker reveals</option>
            <option value="challenging">Push hard: examine everything I say</option>
            <option value="supportive">Gentler pace: questions, but no ambushes</option>
          </select>
        </div>

        {failed && (
          <p className="text-sm sm:text-base text-red-300 bg-red-950/50 border border-red-500/50 rounded-lg px-3 py-2" role="alert">
            {failed}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 px-4 py-3 bg-gold hover:bg-bright-gold text-gunmetal rounded-lg font-semibold disabled:opacity-60 transition-colors"
          >
            {submitting ? 'Saving…' : 'I understand, let’s begin'}
          </button>
          {onDecline && (
            <button type="button" onClick={onDecline} className="px-4 py-3 text-pearl/70 hover:text-white font-medium">
              Not just now
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
